<template>
  <v-main>
    <v-container fluid class="pa-2 pa-sm-4">
      <!-- Back Button -->
      <v-btn
        fab
        x-small
        class="mx-1 mb-2"
        color="white"
        elevation="2"
        @click="$router.back()"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Main Card -->
      <v-row justify="center">
        <v-col cols="12" class="px-0">
          <v-card rounded="xl" color="blue lighten-5" class="pa-3 pa-sm-4">
            <v-container fluid>
              <!-- Header Section -->
              <v-row align="center" class="ma-0">
                <v-col cols="auto" class="pa-0">
                  <h3 class="text-h6 text-sm-h5 py-2">
                    {{
                      user?.roles[0]?.name === "Admin"
                        ? "Add New Bill"
                        : "List of Bills"
                    }}
                  </h3>
                </v-col>
                <v-spacer></v-spacer>
                <v-col
                  cols="auto"
                  class="pa-0"
                  v-if="user?.roles[0]?.name === 'Admin'"
                >
                  <router-link to="/billings/create">
                    <v-btn fab x-small color="white" elevation="2">
                      <v-icon color="blue lighten-1">mdi-plus</v-icon>
                    </v-btn>
                  </router-link>
                </v-col>
              </v-row>

              <!-- Filters -->
              <v-row class="ma-0">
                <v-col cols="6" class="py-0 pr-2">
                  <v-toolbar-title class="text-body-2 text-sm-body-1"
                    >Month</v-toolbar-title
                  >
                  <v-select
                    v-model="selectedFromMonth"
                    :items="months"
                    label="Select Month"
                    solo
                    dense
                    rounded
                    hide-details
                    :disabled="isLoading"
                    @change="getData"
                  ></v-select>
                </v-col>
                <v-col cols="6" class="py-0 pl-2">
                  <v-toolbar-title class="text-body-2 text-sm-body-1"
                    >Year</v-toolbar-title
                  >
                  <v-select
                    v-model="selectedYear"
                    :items="years"
                    label="Select Year"
                    solo
                    dense
                    rounded
                    hide-details
                    :disabled="isLoading"
                    @change="getData"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Divider -->
              <v-divider class="my-3"></v-divider>

              <!-- Billings Table -->
              <v-row class="ma-0">
                <v-col cols="12" class="pa-0">
                  <v-card rounded="lg" elevation="1">
                    <v-data-table
                      :headers="headers"
                      :items="billings"
                      :items-per-page="5"
                      :loading="isLoading"
                      loading-text="Loading bills..."
                      :no-data-text="noDataText"
                      class="elevation-0"
                      mobile-breakpoint="0"
                      dense
                      hide-default-footer
                      :class="{ 'mobile-table': $vuetify.breakpoint.xs }"
                    >
                      <template v-slot:item.index="{ index }">
                        {{ index + 1 }}
                      </template>
                      <template v-slot:item.document_path="{ item }">
                        <a
                          :href="`${mediaUrl + item.document_path}`"
                          target="_blank"
                          rel="noopener noreferrer"
                          v-if="item.document_path"
                          class="text-decoration-none text-truncate"
                        >
                          {{ getFileName(item.document_path) }}
                        </a>
                        <span v-else>No Document</span>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          icon
                          small
                          color="blue lighten-1"
                          @click="$router.push(`/billings/${item.id}`)"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar for Notifications -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        bottom
      >
        {{ snackbarText }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>

    <!-- Footer -->
    <v-container class="footer-text" fluid>
      <v-row justify="center">
        <v-col align="center" cols="12">
          <h3 class="white--text text-body-2">We Alliance Sparkle</h3>
          <h4 class="white--text text-caption">v 1.0.0</h4>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "BillingList",
  data() {
    return {
      isLoading: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      selectedFromMonth: "",
      selectedYear: "",
      billings: [],

      months: [
        { text: "January", value: "01" },
        { text: "February", value: "02" },
        { text: "March", value: "03" },
        { text: "April", value: "04" },
        { text: "May", value: "05" },
        { text: "June", value: "06" },
        { text: "July", value: "07" },
        { text: "August", value: "08" },
        { text: "September", value: "09" },
        { text: "October", value: "10" },
        { text: "November", value: "11" },
        { text: "December", value: "12" },
      ],
      years: [],
      headers: [
        { text: "Sr", value: "index", sortable: false, width: "10%" },
        { text: "Bill", value: "document_path", sortable: true, width: "40%" },
        { text: "Status", value: "status", sortable: true, width: "30%" },
        {
          text: "Action",
          value: "actions",
          sortable: false,
          align: "right",
          width: "20%",
        },
      ],
      noDataText: "No bills found for the selected month and year",
    };
  },
  mounted() {
    this.setCurrentDate();
    this.getMasters();
    this.getData();
  },
  methods: {
    setCurrentDate() {
      const now = new Date();
      this.selectedFromMonth = String(now.getMonth() + 1).padStart(2, "0");
      this.selectedYear = String(now.getFullYear());
    },
    async getMasters() {
      try {
        this.isLoading = true;
        const response = await axios.get("/masters");
        this.years = response.data.data.map((year) => ({
          text: String(year),
          value: String(year),
        }));
      } catch (error) {
        this.showSnackbar("Failed to load years", "error");
        console.error("Error fetching years:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async getData() {
      if (!this.selectedYear) return;
      try {
        this.isLoading = true;
        const response = await axios.get("/billings", {
          params: {
            month: this.selectedFromMonth || undefined,
            year: this.selectedYear,
          },
        });
        this.billings = response.data.data || [];
        if (!this.billings.length) {
          this.showSnackbar("No bills found for the selected period", "info");
        }
      } catch (error) {
        this.showSnackbar("Failed to load bills", "error");
        console.error("Error fetching bills:", error);
      } finally {
        this.isLoading = false;
      }
    },
    getFileName(path) {
      return path ? path.split("/").pop() : "No Document";
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.footer-text {
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: 8px 0;
}

/* Mobile-specific table styling */
.mobile-table {
  font-size: 0.8rem;
}

.mobile-table .v-data-table__wrapper {
  overflow-x: auto;
}

.mobile-table th,
.mobile-table td {
  padding: 0 8px !important;
  height: 36px !important;
}

.mobile-table .text-truncate {
  display: inline-block;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
